import { ACCESS_META, emailInDomain, type PublicUser, type Role } from '@hrms/shared'
import { type FormEvent, useState } from 'react'
import { Avatar } from '../../../components/ui/Avatar.tsx'
import { Button } from '../../../components/ui/Button.tsx'
import { Checkbox, Select, TextInput } from '../../../components/ui/Field.tsx'
import type { PersonRow } from './draft.ts'
import { GridRow, RowActions, SettingsGrid, WithReason } from './SettingsGrid.tsx'

const COLS = 'grid-cols-[minmax(190px,1.2fr)_minmax(200px,1.2fr)_minmax(150px,1fr)_130px_70px_40px]'

const ACCESS_OPTIONS: { role: Role; label: string }[] = [
  { role: 'member', label: ACCESS_META.employee.label },
  { role: 'admin', label: ACCESS_META.hr.label },
  { role: 'viewer', label: ACCESS_META.viewer.label },
]

const ACCESS_HINT = [
  `${ACCESS_META.employee.label}: ${ACCESS_META.employee.description}`,
  `${ACCESS_META.hr.label}: ${ACCESS_META.hr.description}`,
  `${ACCESS_META.viewer.label}: ${ACCESS_META.viewer.description}`,
].join('\n')

export const HR_LEAD_HINT =
  'HR leads see every confidential ticket and decide who has HR access. Only HR team members can be HR leads.'

export interface PeopleSectionProps {
  people: PersonRow[]
  me: PublicUser
  /** The signed-in user is an HR lead (can change access and the HR lead flag). */
  canManageRoles: boolean
  /** Saved role per person id, for "archiving an HR member needs an HR lead". */
  savedRoles: Map<string, Role>
  onChange: (id: string, patch: Partial<PersonRow>) => void
  onRemove: (id: string) => void
}

export function PeopleSection({
  people,
  me,
  canManageRoles,
  savedRoles,
  onChange,
  onRemove,
}: PeopleSectionProps) {
  return (
    <SettingsGrid
      label="People"
      columns={COLS}
      minWidth="min-w-[820px]"
      headers={[
        { label: 'Name', hint: 'Google sign-in may refresh the name from the person’s profile.' },
        {
          label: 'Email',
          hint: 'People sign in with Google using this address. It can’t be changed.',
        },
        { label: 'Team or role' },
        { label: 'Access', hint: ACCESS_HINT },
        { label: 'HR lead', hint: HR_LEAD_HINT, align: 'center' },
        { label: '' },
      ]}
    >
      {people.map((p) => {
        const isMe = p.id === me.id
        const label = p.name || p.email
        const accessBlocked = isMe
          ? 'You can’t change your own access.'
          : !canManageRoles
            ? 'Only an HR lead can change someone’s access.'
            : null
        const leadBlocked = !canManageRoles
          ? 'Only an HR lead can change the HR lead flag.'
          : p.role !== 'admin'
            ? 'Only HR team members can be HR leads.'
            : isMe && p.isHrLead
              ? 'You can’t remove your own HR lead flag.'
              : null
        const wasHr = savedRoles.get(p.id) === 'admin' || p.role === 'admin'
        const archiveBlocked = isMe
          ? 'You can’t archive yourself.'
          : wasHr && !canManageRoles && !p.isNew
            ? 'Only an HR lead can archive or restore HR team members.'
            : null
        return (
          <GridRow
            key={p.id}
            columns={COLS}
            archived={p.archived}
            cells={[
              <span key="n" className="flex min-w-0 items-center gap-2">
                <Avatar name={label} seed={p.id} src={p.avatarUrl} />
                <TextInput
                  size="sm"
                  data-focus-id={p.id}
                  aria-label={`Name of ${p.email}`}
                  value={p.name}
                  maxLength={120}
                  invalid={!p.name.trim()}
                  placeholder="Name"
                  onChange={(e) => onChange(p.id, { name: e.target.value })}
                />
              </span>,
              <span
                key="e"
                className="flex min-w-0 items-center gap-1.5 text-[13px] text-muted"
                title={p.email}
              >
                <span className="truncate">{p.email}</span>
                {isMe && <span className="shrink-0 text-faint">(you)</span>}
                {p.isNew && (
                  <span className="shrink-0 rounded-lozenge bg-green-bg px-1.5 text-xs font-semibold text-green-fg">
                    New
                  </span>
                )}
              </span>,
              <TextInput
                key="d"
                size="sm"
                aria-label={`Team or role of ${label}`}
                value={p.dept}
                maxLength={120}
                placeholder="e.g. Engineering"
                onChange={(e) => onChange(p.id, { dept: e.target.value })}
              />,
              <WithReason key="a" reason={accessBlocked}>
                <Select
                  size="sm"
                  aria-label={`Access for ${label}`}
                  data-tour="people-access"
                  value={p.role}
                  disabled={!!accessBlocked}
                  onChange={(e) => {
                    const role = e.target.value as Role
                    onChange(p.id, { role, isHrLead: role === 'admin' && p.isHrLead })
                  }}
                >
                  {ACCESS_OPTIONS.map((o) => (
                    <option key={o.role} value={o.role}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </WithReason>,
              <span key="l" className="flex justify-center">
                <WithReason reason={leadBlocked}>
                  <Checkbox
                    aria-label={`${label} is an HR lead`}
                    checked={p.role === 'admin' && p.isHrLead}
                    disabled={!!leadBlocked}
                    onChange={(e) => onChange(p.id, { isHrLead: e.target.checked })}
                  />
                </WithReason>
              </span>,
            ]}
            actions={
              <RowActions
                name={label}
                archived={p.archived}
                isNew={p.isNew}
                onArchive={(archived) => onChange(p.id, { archived })}
                onRemove={() => onRemove(p.id)}
                archiveBlocked={archiveBlocked}
              />
            }
          />
        )
      })}
    </SettingsGrid>
  )
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Inline "Add person" row: email is required and must be in the company domain. */
export function AddPersonForm({
  domain,
  existingEmails,
  onAdd,
  onCancel,
}: {
  domain: string | null
  existingEmails: Set<string>
  onAdd: (email: string) => void
  onCancel: () => void
}) {
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)

  function submit(e: FormEvent) {
    e.preventDefault()
    const value = email.trim().toLowerCase()
    if (!EMAIL_RE.test(value)) return setError('Enter an email address.')
    if (!emailInDomain(value, domain)) return setError(`Use a company address (@${domain}).`)
    if (existingEmails.has(value)) return setError('This person is already in the list.')
    onAdd(value)
  }

  return (
    <form
      onSubmit={submit}
      className="flex flex-wrap items-start gap-2 border-t border-border bg-surface-2 px-4 py-3"
      aria-label="Add a person"
    >
      <div className="min-w-[220px] flex-1">
        <label
          htmlFor="add-person-email"
          className="mb-1 block text-[12.5px] font-medium text-muted"
        >
          Work email
        </label>
        <TextInput
          id="add-person-email"
          size="sm"
          type="email"
          autoFocus
          autoComplete="off"
          placeholder={domain ? `name@${domain}` : 'name@company.com'}
          value={email}
          invalid={!!error}
          aria-describedby="add-person-help"
          onChange={(e) => {
            setEmail(e.target.value)
            setError(null)
          }}
          onKeyDown={(e) => e.key === 'Escape' && onCancel()}
        />
        <p
          id="add-person-help"
          className={error ? 'mt-1 text-[12.5px] text-danger' : 'mt-1 text-[12.5px] text-muted'}
        >
          {error ??
            'They’re added as an Employee. When they first sign in with Google, the account links by email.'}
        </p>
      </div>
      <div className="flex gap-2 pt-[22px]">
        <Button type="submit" size="sm" variant="primary">
          Add
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
